import {useState} from 'react';
import {
    Box,
    Button,
    Divider,
    Grid,
    Group,
    TextInput,
    Text,
    useMantineTheme,
    Checkbox, Textarea
} from '@mantine/core';
import {useForm} from '@mantine/form';
import {Affiliate, ArrowRight, Settings, Tags} from "tabler-icons-react";
import {
    QuoteStatusSelect,
    IQuote,
    IQuoteForm,
    QuoteUtils
} from "features/quotes"
import {UsersSelect} from "features/users";
import {AccountsSelect} from "features/accounts";
import {DatePicker} from "@mantine/dates";
import {IProject, ProjectsSelect} from "features/projects";
import {IStrapiEntity} from "services/strapi";

interface Props {
    onSubmitQuery?: (query: any) => void,
    initialValues?: IQuote,
    project?: IStrapiEntity<IProject>
}

export function QuoteCreateForm({onSubmitQuery, initialValues, project}: Props) {

    const [creatingQuote, setCreatingQuote] = useState(false);
    const theme = useMantineTheme()
    const form = useForm<IQuoteForm>({
        initialValues: {
            project: project?.id.toString(),
            account: project?.attributes.account?.data?.id.toString(),
            ...QuoteUtils.transformInitialValues(initialValues)
        },
        validate: QuoteUtils.getValidationSchema(),
    });

    async function onSubmit(values: typeof form.values) {
        if (onSubmitQuery) {
            setCreatingQuote(true);
            await onSubmitQuery(values)
            setCreatingQuote(false);
        }
    }


    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Grid gutter={12}>
                <Grid.Col sm={12}>
                    <Group spacing={6}>
                        <Settings color={theme.colors.green[6]} size={18}/>
                        <Text weight={600}>Details</Text>
                    </Group>
                    <Divider mt={3}/>
                </Grid.Col>
                <Grid.Col sm={12}>
                    <TextInput placeholder="Quote name" required label="Name" {...form.getInputProps('name')}/>
                </Grid.Col>
                <Grid.Col sm={12}>
                    <TextInput required label="Quote ID" {...form.getInputProps('externalId')}/>
                    <Text mt={6} size="xs" color="dimmed">Quote IDs are auto-generated, but can be overridden manually
                        here. Quote IDs are unique across all quotes for Mark III. The Quote ID is generally used for
                        internal use for Mark III only.</Text>
                </Grid.Col>
                <Grid.Col sm={12}>
                    <QuoteStatusSelect required label="Status" {...form.getInputProps('status')}/>
                </Grid.Col>
                <Grid.Col sm={12}>
                    <UsersSelect required label="Inside Sales Rep" {...form.getInputProps('insideSalesRep')}/>
                </Grid.Col>
                <Grid.Col sm={12}>
                    <Group spacing={6}>
                        <Affiliate color={theme.colors.green[6]} size={18}/>
                        <Text weight={600}>Account</Text>
                    </Group>
                    <Divider mt={3}/>
                    <Text mt={6} color="dimmed" italic size="sm">
                        Account Executives, Contacts, and Projects are loaded from the chosen Account. Please choose
                        an Account first.
                    </Text>
                </Grid.Col>
                <Grid.Col sm={12}>
                    <AccountsSelect required label="Account" {...form.getInputProps('account')}/>
                </Grid.Col>
                <Grid.Col sm={12}>
                    <ProjectsSelect
                        required
                        account={form.values.account}
                        label="Project"
                        {...form.getInputProps('project')}/>
                </Grid.Col>
                <Grid.Col sm={6}>
                    <UsersSelect
                        required
                        label="Mark III Account Executive"
                        {...form.getInputProps('salesRep')}
                        disabled={!form.values.account}
                        filters={{
                            salesRepAccounts: {
                                id: {
                                    $in: [form.values.account]
                                }
                            }
                        }}
                    />
                </Grid.Col>
                <Grid.Col sm={6}>
                    <UsersSelect
                        label="Primary Account Contact"
                        {...form.getInputProps('contact')}
                        disabled={!form.values.account}
                        filters={{
                            account: {
                                id: {
                                    $eq: form.values.account
                                }
                            }
                        }}
                    />
                </Grid.Col>
                <Grid.Col sm={12}>
                    <Group spacing={6} mt={18}>
                        <Tags color={theme.colors.green[6]} size={18}/>
                        <Text weight={600}>Metadata</Text>
                    </Group>
                    <Divider mt={3}/>
                </Grid.Col>
                <Grid.Col sm={6}>
                    <DatePicker label="Expiration Date" {...form.getInputProps('expirationDate')}/>
                </Grid.Col>
                <Grid.Col sm={6}>
                    <DatePicker label="Follow Up Date" {...form.getInputProps('followUp')}/>
                </Grid.Col>
                <Grid.Col sm={12}>
                    <Textarea label="Notes" {...form.getInputProps('notes')}/>
                </Grid.Col>

                <Grid.Col sm={12}>
                    <Checkbox
                        label="Standard"
                        {...form.getInputProps('isStandard', {type: 'checkbox'})}
                    />
                </Grid.Col>
                <Grid.Col sm={12}>
                    <Group position="right" mt="md">
                        <Button type="submit" loading={creatingQuote}
                                rightIcon={<ArrowRight/>}>
                            {initialValues ? 'Edit Quote' : 'Add New Quote'}
                        </Button>
                    </Group>
                </Grid.Col>
            </Grid>
        </form>
    )
}
